import { useState, useEffect, useRef } from 'react'
import { Camera, FolderOpen, Plus, Upload, Download, Trash2, Lock, Unlock, Loader2, AlertCircle, ArrowLeft, Image, File, X } from 'lucide-react'
import { nasAPI, authAPI } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog'

const formatSize = (bytes) => {
  if (!bytes) return '–'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const formatDate = (dt) => new Date(dt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })

export default function EventMediaPage() {
  const [user, setUser] = useState(null)
  const [folders, setFolders] = useState([])
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [files, setFiles] = useState([])
  const [isLoadingFolders, setIsLoadingFolders] = useState(true)
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [error, setError] = useState(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [lightbox, setLightbox] = useState(null)
  const [downloading, setDownloading] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    authAPI.getCurrentUser().then(r => setUser(r.data.user)).catch(() => {})
    loadFolders()
  }, [])

  const loadFolders = async () => {
    setIsLoadingFolders(true)
    setError(null)
    try {
      const res = await nasAPI.getMediaFolders()
      setFolders(res.data.folders)
    } catch (err) {
      setError(err.response?.data?.error || 'Kan mappen niet laden')
    } finally {
      setIsLoadingFolders(false)
    }
  }

  const openFolder = async (folder) => {
    setSelectedFolder(folder)
    setIsLoadingFiles(true)
    setError(null)
    try {
      const res = await nasAPI.listMediaFiles(folder.id)
      setFiles(res.data.files)
    } catch (err) {
      setError(err.response?.data?.error || 'Kan bestanden niet laden')
    } finally {
      setIsLoadingFiles(false)
    }
  }

  const createFolder = async () => {
    if (!newFolderName.trim()) return
    setIsCreating(true)
    try {
      await nasAPI.createMediaFolder(newFolderName)
      setNewFolderName('')
      setCreateDialogOpen(false)
      await loadFolders()
    } catch (err) {
      setError(err.response?.data?.error || 'Aanmaken mislukt')
    } finally {
      setIsCreating(false)
    }
  }

  const toggleFolder = async (folder) => {
    try {
      await nasAPI.toggleMediaFolder(folder.id)
      await loadFolders()
      if (selectedFolder?.id === folder.id) {
        setSelectedFolder(prev => ({ ...prev, is_open: !prev.is_open }))
      }
    } catch {
      setError('Bijwerken mislukt')
    }
  }

  const deleteFolder = async (folder) => {
    if (!confirm(`Map "${folder.name}" verwijderen uit de lijst? (Bestanden op NAS blijven bewaard)`)) return
    try {
      await nasAPI.deleteMediaFolder(folder.id)
      await loadFolders()
      if (selectedFolder?.id === folder.id) {
        setSelectedFolder(null)
        setFiles([])
      }
    } catch {
      setError('Verwijderen mislukt')
    }
  }

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files)
    if (!fileList.length) return
    setIsUploading(true)
    setError(null)
    let done = 0
    for (const file of fileList) {
      setUploadProgress(`${file.name} (${done + 1}/${fileList.length})`)
      try {
        await nasAPI.uploadMediaFile(selectedFolder.id, file)
      } catch (err) {
        setError(`Upload mislukt: ${file.name}`)
      }
      done++
    }
    setIsUploading(false)
    setUploadProgress(null)
    e.target.value = ''
    await openFolder(selectedFolder)
  }

  const handleDownload = async (filename) => {
    setDownloading(filename)
    try {
      const res = await nasAPI.downloadMediaFile(selectedFolder.id, filename)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      setError('Download mislukt')
    } finally {
      setDownloading(null)
    }
  }

  const deleteFile = async (filename) => {
    if (!confirm(`"${filename}" verwijderen van de NAS?`)) return
    try {
      await nasAPI.deleteMediaFile(selectedFolder.id, filename)
      setFiles(prev => prev.filter(f => f.name !== filename))
    } catch {
      setError('Verwijderen mislukt')
    }
  }

  const getPreviewUrl = (filename) => {
    const token = localStorage.getItem('auth_token')
    return `${nasAPI.previewMediaFileUrl(selectedFolder.id, filename)}&token=${token}`
  }

  const imageFiles = files.filter(f => f.isImage)
  const otherFiles = files.filter(f => !f.isImage)
  const isAdmin = ['admin','superadmin'].includes(user?.role)

  if (selectedFolder) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedFolder(null); setFiles([]) }}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Terug
          </Button>
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-purple to-neon-blue flex items-center justify-center">
            <Camera className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{selectedFolder.name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant={selectedFolder.is_open ? 'default' : 'secondary'}>
                {selectedFolder.is_open ? 'Open voor uploads' : 'Gesloten'}
              </Badge>
              <span className="text-muted-foreground text-sm">{files.length} bestand{files.length !== 1 ? 'en' : ''}</span>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Upload */}
        {selectedFolder.is_open && (
          <div className="mb-6 p-4 rounded-lg border border-dashed border-neon-blue/40 bg-neon-blue/5">
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.zip,.rar,.7z"
                onChange={handleUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="bg-neon-blue hover:bg-neon-blue/80"
              >
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {isUploading ? 'Uploaden...' : 'Bestanden uploaden'}
              </Button>
              {uploadProgress && <span className="text-sm text-muted-foreground">{uploadProgress}</span>}
              <span className="text-xs text-muted-foreground">Foto's, video's en archieven</span>
            </div>
          </div>
        )}

        {isLoadingFiles ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-neon-blue" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Camera className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Nog geen bestanden in deze map</p>
            {selectedFolder.is_open && <p className="text-sm mt-1">Upload de eerste bestanden hierboven</p>}
          </div>
        ) : (
          <>
            {/* Image gallery */}
            {imageFiles.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Image className="h-5 w-5 text-neon-blue" /> Foto's ({imageFiles.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {imageFiles.map(file => (
                    <div key={file.name} className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-muted/20">
                      <img
                        src={getPreviewUrl(file.name)}
                        alt={file.name}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                        onClick={() => setLightbox(file.name)}
                        onError={e => { e.target.style.display = 'none' }}
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                        <div className="flex gap-1 w-full">
                          <button
                            onClick={() => handleDownload(file.name)}
                            className="flex-1 flex items-center justify-center gap-1 bg-neon-blue/80 rounded px-2 py-1 text-xs text-white hover:bg-neon-blue"
                            disabled={downloading === file.name}
                          >
                            {downloading === file.name ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => deleteFile(file.name)}
                              className="flex items-center justify-center bg-red-500/80 rounded px-2 py-1 text-xs text-white hover:bg-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="absolute top-1 right-1 bg-black/50 rounded px-1 text-xs text-white opacity-0 group-hover:opacity-100">
                        {formatSize(file.size)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other files */}
            {otherFiles.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <File className="h-5 w-5 text-neon-blue" /> Overige bestanden ({otherFiles.length})
                </h2>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Naam</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground w-28">Grootte</th>
                        <th className="w-24 px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherFiles.map(file => (
                        <tr key={file.name} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                          <td className="px-4 py-3 flex items-center gap-2">
                            <File className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{file.name}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-muted-foreground">{formatSize(file.size)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(file.name)} disabled={downloading === file.name}>
                                {downloading === file.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                              </Button>
                              {isAdmin && (
                                <Button variant="ghost" size="sm" onClick={() => deleteFile(file.name)} className="text-red-400 hover:text-red-300">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Lightbox */}
        {lightbox && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setLightbox(null)}>
              <X className="h-8 w-8" />
            </button>
            <img
              src={getPreviewUrl(lightbox)}
              alt={lightbox}
              className="max-w-full max-h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-sm">{lightbox}</div>
          </div>
        )}
      </div>
    )
  }

  // Folder list view
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-purple to-neon-blue flex items-center justify-center">
            <Camera className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Event Media</h1>
            <p className="text-muted-foreground text-sm">Foto's en bestanden per evenement</p>
          </div>
        </div>
        {isAdmin && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-neon-blue hover:bg-neon-blue/80">
                <Plus className="h-4 w-4 mr-2" /> Nieuwe map
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Event map aanmaken</DialogTitle>
                <DialogDescription>Maak een nieuwe map aan op de NAS voor event media.</DialogDescription>
              </DialogHeader>
              <Input
                placeholder="Naam (bijv. LAN Mei 2025)"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createFolder()}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuleren</Button>
                <Button onClick={createFolder} disabled={isCreating || !newFolderName.trim()}>
                  {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Aanmaken
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoadingFolders ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-neon-blue" />
        </div>
      ) : folders.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Camera className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nog geen event mappen aangemaakt</p>
          {isAdmin && <p className="text-sm mt-1">Klik op "Nieuwe map" om te beginnen</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map(folder => (
            <div
              key={folder.id}
              className="rounded-lg border border-border bg-card hover:border-neon-blue/40 transition-colors"
            >
              <button
                className="w-full p-5 text-left"
                onClick={() => openFolder(folder)}
              >
                <div className="flex items-start gap-3">
                  <FolderOpen className={`h-8 w-8 mt-0.5 shrink-0 ${folder.is_open ? 'text-yellow-400' : 'text-muted-foreground'}`} />
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{folder.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(folder.created_at)}</p>
                    <Badge variant={folder.is_open ? 'default' : 'secondary'} className="mt-2 text-xs">
                      {folder.is_open ? 'Open' : 'Gesloten'}
                    </Badge>
                  </div>
                </div>
              </button>
              {isAdmin && (
                <div className="border-t border-border px-4 py-2 flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleFolder(folder)}
                    title={folder.is_open ? 'Sluiten' : 'Openen'}
                  >
                    {folder.is_open ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteFolder(folder)}
                    className="text-red-400 hover:text-red-300"
                    title="Verwijderen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
